<template>
  <div class="experience-page">
    <template v-if="isPending"></template>

    <div v-else-if="!assessment" class="experience-content">
      <div class="page-header">
        <h1>{{ $t('app.experience.detail.notFound') }}</h1>
      </div>
      <div>
        <Button class="primary" :label="$t('app.experience.detail.backToList')" @click="router.push({ name: 'invitations' })" />
      </div>
    </div>

    <div v-else class="experience-content">
      <div class="title-row">
        <div class="title">
          <button type="button" class="back-button" :aria-label="$t('app.experience.detail.backToList')" @click="router.push({ name: statusRouteName[status] })">
            <i class="bi bi-arrow-left"></i>
          </button>
          <h1>{{ assessment.campaign.campaign.headline }}</h1>
        </div>
        <div class="align-right">
          <span class="meta-progress">
            {{ $t('app.experience.detail.progress', { completed: completedCount, total: situations.length }) }}
            <span class="exp-progress">
              <span class="exp-progress-value" :class="{ done: isFinished }" :style="{ width: `${progress}%` }"></span>
            </span>
          </span>
          <Button v-if="isFinished" class="primary icon icon-left m" icon="bi-download" :label="$t('app.experience.detail.downloadResults')" @click="notAvailable" />
        </div>
      </div>

      <div class="meta">
        <!-- <span>{{ $t('app.experience.detail.job') }} <strong>{{ assessment.campaign.campaign.job_title.length ? assessment.campaign.campaign.job_title : $t('app.experience.detail.undefinedJob') }}</strong></span> -->
        <span>{{ $t('app.experience.detail.invitedAt', { date: formatDate(assessment.creation_time, locale) }) }}</span>
      </div>

      <div v-if="isFinished" class="finished-banner">
        <i class="bi bi-check-lg"></i>
        <span>{{ $t('app.experience.detail.finishedBanner') }}</span>
      </div>

      <section class="info-banner" :aria-label="$t('app.experience.detail.info.title')">
        <i class="bi bi-exclamation-triangle"></i>
        <div class="info-content">
          <div class="info-title">{{ $t('app.experience.detail.info.title') }}</div>
          <i18n-t keypath="app.experience.detail.info.characters" tag="div">
            <template #highlight><strong>{{ $t('app.experience.detail.info.charactersHighlight') }}</strong></template>
          </i18n-t>
          <i18n-t keypath="app.experience.detail.info.voice" tag="div">
            <template #highlight><strong>{{ $t('app.experience.detail.info.voiceHighlight') }}</strong></template>
          </i18n-t>
          <div>
            <i18n-t keypath="app.experience.detail.info.setup" tag="span">
              <template #highlight><strong>{{ $t('app.experience.detail.info.setupHighlight') }}</strong></template>
            </i18n-t>
            <button type="button" class="info-icon" :aria-label="$t('app.experience.detail.setupPanel.open')" @click="setupPanelOpen = true">
              <i class="bi bi-info-circle bold"></i>
            </button>
          </div>
          <i18n-t keypath="app.experience.detail.info.instructions" tag="div">
            <template #highlight><strong>{{ $t('app.experience.detail.info.instructionsHighlight') }}</strong></template>
          </i18n-t>
        </div>
      </section>

      <section v-if="assessment.campaign.assessment.description" class="exp-card context">
        <button type="button" class="context-header" :aria-expanded="contextOpen" @click="contextOpen = !contextOpen">
          <span>{{ $t('app.experience.detail.context') }}</span>
          <i class="bi bi-chevron-down chevron" :class="{ open: contextOpen }"></i>
        </button>
        <p v-if="contextOpen" v-safe-html="assessment.campaign.assessment.description" class="context-body"></p>
      </section>

      <div class="steps-header">
        <h2>{{ $t('app.experience.detail.exercises') }}</h2>
        <span>{{ $t('app.experience.detail.stepsCount', situations.length) }}</span>
      </div>

      <div class="steps">
        <SituationStep
          v-for="(situation, index) in situations"
          :key="situation.id"
          :situation="situation"
          :index="index"
          :open="openSteps.has(situation.id)"
          :done="completedIds.has(situation.id)"
          :live="liveIndex === index"
          :locked="!!liveSituationId && liveSituationId !== situation.id"
          :finished="isFinished"
          @toggle="toggleStep(situation.id)"
          @start="startConversation(index)"
          @show-conversation="panelMinimized = false"
        />
      </div>

      <div v-if="!isFinished" class="exp-card end-card">
        <div class="end-text">
          <span class="end-title">{{ $t('app.experience.detail.endCard.title') }}</span>
          <span>{{ $t('app.experience.detail.endCard.text') }}</span>
        </div>
        <Button class="secondary icon icon-left" icon="bi-flag" :label="$t('app.experience.detail.endAssessment')" :loading="isCompleting" :disabled="!canEnd || isCompleting" v-tooltip="endTooltip" @click="confirmOpen = true" />
      </div>
    </div>

    <ConversationPanel
      v-if="liveSituation && assessment"
      :situation="liveSituation"
      :participation-id="assessment.participant_id"
      :index="liveIndex"
      :minimized="panelMinimized"
      @minimize="panelMinimized = true"
      @restore="panelMinimized = false"
      @close="closePanel"
      @end-step="endStep"
      @conversation-ended="onConversationEnded"
      @conversation-error="onConversationError"
    />

    <Modal
      :isOpen="confirmOpen"
      :title="$t('app.experience.detail.confirm.title')"
      :text="confirmText"
      :buttons="confirmButtons"
      @close="closeConfirm"
      @callbackBtn1="closeConfirm"
      @callbackBtn2="endAssessment"
    >
      <template #extra>
        <p>{{ $t('app.experience.detail.confirm.definitive') }}</p>
      </template>
    </Modal>

    <SetupPanel :isOpen="setupPanelOpen" :micEnabled="!liveSituationId && !isFinished" @close="setupPanelOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { useToast } from "vue-toastification";
import { useQueryClient } from "@tanstack/vue-query";
import { useAssessmentTracking, useCandidateAssessmentsV4, useCompleteParticipation } from '@/queries/assessment.query';
import { formatDate, getAssessmentStatus, statusRouteName } from './assessmentStatus';
import SituationStep from './partials/SituationStep.vue';
import ConversationPanel from './partials/ConversationPanel.vue';
import SetupPanel from './partials/SetupPanel.vue';

const props = defineProps<{
  participantId: string,
}>();

const router = useRouter();
const toast = useToast();
const queryClient = useQueryClient();
const { t, locale } = useI18n();

const { data: assessments, isPending } = useCandidateAssessmentsV4();
const { data: tracking } = useAssessmentTracking(computed(() => props.participantId));
const { mutate: completeParticipation, isPending: isCompleting } = useCompleteParticipation();

const assessment = computed(() => assessments.value?.find((a) => a.participant_id === props.participantId));
const status = computed(() => assessment.value ? getAssessmentStatus(assessment.value) : 'invited');
const isFinished = computed(() => status.value === 'finished');

const situations = computed(() => assessment.value?.campaign.assessment.situations || []);
const completedIds = computed(() => new Set(tracking.value?.completed_situations.map((c) => c.situation) || []));
const completedCount = computed(() => situations.value.filter((s) => completedIds.value.has(s.id)).length);
const progress = computed(() => situations.value.length ? Math.round((completedCount.value / situations.value.length) * 100) : 0);

// A situation is required when at least one required skill is evaluated in it
const requiredSituationIds = computed(() => new Set(
  (assessment.value?.campaign.assessment.skills || []).filter((s) => s.required).flatMap((s) => s.situations),
));
const missingRequired = computed(() => situations.value.filter((s) => requiredSituationIds.value.has(s.id) && !completedIds.value.has(s.id)));
const canEnd = computed(() => missingRequired.value.length === 0);
const endTooltip = computed(() => {
  if (canEnd.value) return null;
  const names = missingRequired.value.map((s) => s.ui?.name || s.id).join(', ');
  return t('app.experience.detail.endCard.requiredMissing', { names });
});

const contextOpen = ref(true);
const openSteps = ref(new Set<string>());
const liveSituationId = ref<string | null>(null);
const panelMinimized = ref(false);
const confirmOpen = ref(false);
const setupPanelOpen = ref(false);

const liveSituation = computed(() => situations.value.find((s) => s.id === liveSituationId.value) ?? null);
const liveIndex = computed(() => situations.value.findIndex((s) => s.id === liveSituationId.value));

function toggleStep(situationId: string) {
  const next = new Set(openSteps.value);
  if (next.has(situationId)) next.delete(situationId);
  else next.add(situationId);
  openSteps.value = next;
}

function notAvailable() {
  toast.info(t('app.experience.notAvailable'));
}

// Triggers the browser microphone permission prompt; the stream is released right away
async function requestMicrophone(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false; // insecure context / unsupported browser
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

// TODO: start the conversation through the v4 backend
async function startConversation(index: number) {
  if (liveSituationId.value) return;
  if (!(await requestMicrophone())) {
    toast.error(t('app.experience.detail.micDenied'));
    return;
  }
  // Another conversation may have been started while the permission prompt was open
  if (liveSituationId.value) return;
  liveSituationId.value = situations.value[index].id;
  panelMinimized.value = false;
}

// The conversation could not be launched: the situation is no longer considered live
function closePanel() {
  liveSituationId.value = null;
  panelMinimized.value = false;
}

// TODO: complete the situation through the v4 backend
function endStep() {
  liveSituationId.value = null;
  panelMinimized.value = false;
}

// Closes the conversation panel and refreshes the assessment state (completed situations, status)
function closeConversation() {
  liveSituationId.value = null;
  panelMinimized.value = false;
  queryClient.invalidateQueries({ queryKey: ['candidate-assessments', 'v4'] });
  queryClient.invalidateQueries({ queryKey: ['assessment-tracking', props.participantId] });
}

function onConversationEnded() {
  closeConversation();
}

function onConversationError(message: string) {
  console.error("Conversation error:", message);
  closeConversation();
  toast.error(t('app.experience.detail.conversationError'));
}

// The confirm modal stays open while the completion request is pending
function closeConfirm() {
  if (isCompleting.value) return;
  confirmOpen.value = false;
}

function endAssessment() {
  if (!canEnd.value || isCompleting.value) return;
  completeParticipation(props.participantId, {
    onSuccess: () => {
      confirmOpen.value = false;
      liveSituationId.value = null;
    },
    onError: () => toast.error(t('app.experience.detail.confirm.error')),
  });
}

const confirmText = computed(() => {
  const left = situations.value.length - completedCount.value;
  if (left > 0) return t('app.experience.detail.confirm.remaining', { count: left }, left);
  return t('app.experience.detail.confirm.allDone');
});

const confirmButtons = computed(() => ({
  column: false,
  data: [
    { label: t('app.experience.detail.confirm.cancel'), class: 'outline secondary', disabled: isCompleting.value },
    { label: t('app.experience.detail.endAssessment'), class: 'secondary', loading: isCompleting.value },
  ],
}));
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";
@import "./experience.scss";

.breadcrumb {
  font-size: 12px;
  color: var(--grey-50-color);
}

.title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    h1 {
      margin: 0;
      font-size: 27px;
      font-weight: 700;
      letter-spacing: -0.4px;
    }
  }

  .align-right {
    display: flex;
    gap: 24px;
    align-items: center;
    margin-left: auto;
  }

  .back-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background-color: $primary-color;
    color: $secondary-color;
    font-size: 17px;
    padding: 0;
    cursor: pointer;
  }

  .meta-progress {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: var(--grey-50-color);
  
    .exp-progress-value {
      display: block;
    }
  }
}

.meta {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 13px;
  color: var(--grey-50-color);

  strong {
    font-weight: 600;
    color: var(--secondary-color);
  }
}

.finished-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid $green-200-color;
  border-radius: 8px;
  background-color: $green-50-color;
  color: $green-800-color;
  font-size: 13px;
}

.info-banner {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid $primary-400-color;
  border-radius: 8px;
  background-color: $primary-100-color;
  color: $secondary-color;

  > i {
    flex-shrink: 0;
    font-size: 19px;
  }

  .info-content {
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-size: 13px;
    //line-height: 1.55;
  }

  .info-title {
    font-weight: 700;
  }

  .info-icon {
    margin-left: 4px;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font-size: 14px;
    vertical-align: middle;
    cursor: pointer;
  }
}

.context {
  .context-header {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border: none;
    background: none;
    color: inherit;
    font-family: inherit;
    font-size: 16px;
    font-weight: 700;
    text-align: left;
    cursor: pointer;

    span {
      flex-grow: 1;
    }
  }

  .context-body {
    margin: 0;
    padding: 0 20px 20px;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-line;
  }
}

.chevron {
  font-size: 17px;
  transition: transform .2s;

  &.open {
    transform: rotate(180deg);
  }
}

.steps-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 8px;

  h2 {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
  }

  span {
    font-size: 12px;
    color: var(--grey-50-color);
  }
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.end-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px;

  .end-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: var(--grey-50-color);
  }

  .end-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--secondary-color);
  }
}
</style>
